import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  MessageCircle, X, Send, Loader2, Minimize2, Maximize2,
  User, Building, ChevronLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatWidget = () => {
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [conversas, setConversas] = useState([]);
  const [conversaAtual, setConversaAtual] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [totalNaoLidas, setTotalNaoLidas] = useState(0);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    if (token && user) {
      fetchNaoLidas();
      // Poll para novas mensagens a cada 10 segundos
      pollIntervalRef.current = setInterval(fetchNaoLidas, 10000);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [token, user]);

  useEffect(() => {
    if (isOpen && conversaAtual) {
      fetchMensagens();
      // Poll para mensagens da conversa atual
      const msgPoll = setInterval(fetchMensagens, 5000);
      return () => clearInterval(msgPoll);
    }
  }, [isOpen, conversaAtual]);

  useEffect(() => {
    scrollToBottom();
  }, [mensagens]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchNaoLidas = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/nao-lidas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTotalNaoLidas(data.total_nao_lidas);
      }
    } catch (error) {
      console.error('Erro ao buscar não lidas:', error);
    }
  };

  const fetchConversas = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/conversas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setConversas(await res.json());
      }
    } catch (error) {
      console.error('Erro ao buscar conversas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMensagens = async () => {
    if (!conversaAtual) return;
    
    try {
      const res = await fetch(`${API_URL}/api/chat/conversas/${conversaAtual.id}/mensagens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setMensagens(await res.json());
        fetchNaoLidas();
      }
    } catch (error) {
      console.error('Erro ao buscar mensagens:', error);
    }
  };

  const iniciarConversaAdmin = async () => {
    setLoading(true);
    try {
      // Buscar admin da plataforma
      const adminRes = await fetch(`${API_URL}/api/usuarios?perfil=admin_plataforma`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let adminId = null;
      if (adminRes.ok) {
        const admins = await adminRes.json();
        if (admins.length > 0) {
          adminId = admins[0].id;
        }
      }
      
      if (!adminId) {
        // Usar ID fixo do admin se não encontrar
        adminId = "admin-plataforma";
      }
      
      // Criar ou obter conversa
      const res = await fetch(
        `${API_URL}/api/chat/conversas?participante_id=${adminId}&tipo=empresa_admin&assunto=Contato via Chat`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        const conversa = await res.json();
        setConversaAtual(conversa);
        fetchMensagens();
      }
    } catch (error) {
      console.error('Erro ao iniciar conversa:', error);
    } finally {
      setLoading(false);
    }
  };

  const enviarMensagem = async (e) => {
    e.preventDefault();
    if (!novaMensagem.trim() || !conversaAtual) return;
    
    setEnviando(true);
    try {
      const res = await fetch(
        `${API_URL}/api/chat/conversas/${conversaAtual.id}/mensagens?texto=${encodeURIComponent(novaMensagem)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        setNovaMensagem('');
        fetchMensagens();
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    } finally {
      setEnviando(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
    fetchConversas();
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const today = new Date();
      if (date.toDateString() === today.toDateString()) {
        return 'Hoje';
      }
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch {
      return '';
    }
  };

  // Não mostrar para admin da plataforma (eles usam a página dedicada)
  if (user?.perfil === 'admin_plataforma') {
    return null;
  }

  return (
    <>
      {/* Botão Flutuante */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-6 w-14 h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 z-50"
          data-testid="chat-widget-button"
        >
          <MessageCircle className="w-6 h-6" />
          {totalNaoLidas > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
              {totalNaoLidas > 9 ? '9+' : totalNaoLidas}
            </span>
          )}
        </button>
      )}

      {/* Janela do Chat */}
      {isOpen && (
        <div 
          className={`fixed bottom-6 right-6 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 transition-all duration-300 ${
            isMinimized ? 'w-72 h-14' : 'w-96 h-[500px]'
          }`}
          data-testid="chat-widget-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-emerald-600 text-white rounded-t-2xl">
            <div className="flex items-center gap-2">
              {conversaAtual && (
                <button 
                  onClick={() => setConversaAtual(null)}
                  className="hover:bg-white/20 p-1 rounded"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <MessageCircle className="w-5 h-5" />
              <span className="font-semibold">
                {conversaAtual ? 'Chat com Suporte' : 'Mensagens'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => setIsMinimized(!isMinimized)}
                className="hover:bg-white/20 p-1.5 rounded"
              >
                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </button>
              <button 
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/20 p-1.5 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          {!isMinimized && (
            <div className="flex flex-col h-[calc(100%-56px)]">
              {!conversaAtual ? (
                // Lista de Conversas
                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                    </div>
                  ) : conversas.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                        <MessageCircle className="w-8 h-8 text-emerald-600" />
                      </div>
                      <h3 className="font-semibold text-slate-800 mb-2">Fale Conosco</h3>
                      <p className="text-sm text-slate-500 mb-4">
                        Entre em contato com nossa equipe para negociar módulos ou tirar dúvidas.
                      </p>
                      <Button 
                        onClick={iniciarConversaAdmin}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        Iniciar Conversa
                      </Button>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {conversas.map((conv) => (
                        <button
                          key={conv.id}
                          onClick={() => {
                            setConversaAtual(conv);
                            fetchMensagens();
                          }}
                          className="w-full p-4 text-left hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Building className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-slate-800 truncate">
                                  {conv.participante_2_nome || 'Suporte'}
                                </span>
                                <span className="text-xs text-slate-400">
                                  {formatDate(conv.ultima_mensagem_data)}
                                </span>
                              </div>
                              <p className="text-sm text-slate-500 truncate">
                                {conv.ultima_mensagem || conv.assunto || 'Nova conversa'}
                              </p>
                            </div>
                            {conv.nao_lidas > 0 && (
                              <Badge className="bg-emerald-600 text-white">{conv.nao_lidas}</Badge>
                            )}
                          </div>
                        </button>
                      ))}
                      
                      {/* Botão para nova conversa */}
                      <div className="p-4">
                        <Button 
                          onClick={iniciarConversaAdmin}
                          variant="outline"
                          className="w-full"
                        >
                          Nova Conversa
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Conversa Atual
                <>
                  {/* Mensagens */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {mensagens.length === 0 ? (
                      <div className="text-center text-slate-400 text-sm py-8">
                        Nenhuma mensagem ainda. Envie a primeira!
                      </div>
                    ) : (
                      mensagens.map((msg) => {
                        const isMe = msg.remetente_id === user?.id;
                        return (
                          <div
                            key={msg.id}
                            className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                                isMe
                                  ? 'bg-emerald-600 text-white rounded-br-md'
                                  : 'bg-slate-100 text-slate-800 rounded-bl-md'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap">{msg.texto}</p>
                              <p className={`text-xs mt-1 ${isMe ? 'text-emerald-200' : 'text-slate-400'}`}>
                                {formatTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <form onSubmit={enviarMensagem} className="p-3 border-t bg-slate-50 rounded-b-2xl">
                    <div className="flex gap-2">
                      <Input
                        value={novaMensagem}
                        onChange={(e) => setNovaMensagem(e.target.value)}
                        placeholder="Digite sua mensagem..."
                        className="flex-1"
                        disabled={enviando}
                      />
                      <Button 
                        type="submit" 
                        size="icon"
                        disabled={enviando || !novaMensagem.trim()}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {enviando ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
